import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Search, X, SlidersHorizontal, ChevronLeft } from "lucide-react";

const DEFAULT_STATE = {
  sport: "all", brand: "", brand_line: "", grader: "", condition_group: "",
  attrs: [], year_from: "", year_to: "", set_name: "", player: "", team: "",
  parallel: "", q: "", sort: "recent",
};

function paramsToState(params) {
  const grader = params.get("grader") || "";
  const explicitCg = params.get("condition_group") || "";
  return {
    ...DEFAULT_STATE,
    sport: params.get("sport") || "all",
    brand: params.get("brand") || "",
    brand_line: params.get("brand_line") || "",
    grader,
    // Auto-set condition_group=graded when grader is present (so Grader section stays visible)
    condition_group: explicitCg || (grader && grader !== "raw" ? "graded" : ""),
    q: params.get("q") || "",
  };
}

export default function Shop() {
  const [params] = useSearchParams();
  const [sports, setSports] = useState([]);
  const [attrOpts, setAttrOpts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brandLines, setBrandLines] = useState([]);
  const [graders, setGraders] = useState([]);
  const [facets, setFacets] = useState({ player: [], team: [], set_name: [], parallel: [], year: [] });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Initialize state from URL query params (so nav dropdowns can deep-link)
  const [state, setState] = useState(() => paramsToState(params));

  // Re-sync state whenever the URL query params change (deep-link nav from same route)
  useEffect(() => {
    setState(paramsToState(params));
     
  }, [params]);

  useEffect(() => {
    Promise.all([
      api.get("/filters?kind=sport"),
      api.get("/filters?kind=attribute"),
      api.get("/filters?kind=brand"),
      api.get("/filters?kind=brand_line"),
      api.get("/filters?kind=grader"),
      api.get("/products/facets?category=singles"),
    ]).then(([s, a, b, bl, g, f]) => {
      setSports(s.data); setAttrOpts(a.data); setBrands(b.data);
      setBrandLines(bl.data); setGraders(g.data); setFacets(f.data);
    }).catch((err) => console.warn("Filter/facet load:", err?.message));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams();
    p.append("category", "singles");
    if (state.sport !== "all") p.append("sport", state.sport);
    state.attrs.forEach((a) => p.append("attribute", a));
    if (state.brand) p.append("brand", state.brand);
    if (state.brand_line) p.append("brand_line", state.brand_line);
    if (state.grader) p.append("grader", state.grader);
    if (state.condition_group) p.append("condition_group", state.condition_group);
    if (state.year_from) p.append("year_from", state.year_from);
    if (state.year_to) p.append("year_to", state.year_to);
    if (state.set_name) p.append("set_name", state.set_name);
    if (state.player) p.append("player", state.player);
    if (state.team) p.append("team", state.team);
    if (state.parallel) p.append("parallel", state.parallel);
    if (state.q.trim()) p.append("q", state.q.trim());
    p.append("sort", state.sort);
    api.get(`/products?${p.toString()}`)
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [state]);

  const update = (k, v) => setState((s) => ({ ...s, [k]: v }));
  const toggleAttr = (k) => setState((s) => ({
    ...s, attrs: s.attrs.includes(k) ? s.attrs.filter((x) => x !== k) : [...s.attrs, k],
  }));

  const filtersActive = useMemo(() => {
    return state.sport !== "all" || state.brand || state.brand_line || state.grader ||
           state.condition_group || state.attrs.length > 0 || state.year_from || state.year_to ||
           state.set_name || state.player || state.team || state.parallel || state.q.trim();
  }, [state]);

  const clearAll = () => setState(DEFAULT_STATE);

  const linesForBrand = brandLines.filter((bl) => bl.parent_key === state.brand);

  return (
    <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-10">
      <div className="text-label text-zinc-500">Marketplace</div>
      <h1 className="font-display text-5xl sm:text-6xl mt-2">Shop Cards</h1>
      <p className="text-zinc-400 mt-2 text-sm max-w-2xl">Every card ships with a Card Lobster authenticity mark. Filter by sport, brand, condition, and more.</p>

      {/* Prominent search bar */}
      <div className="mt-6 relative max-w-3xl">
        <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          data-testid="shop-search"
          type="search"
          placeholder="Search by Player or Team…"
          value={state.q}
          onChange={(e) => update("q", e.target.value)}
          className="w-full bg-[var(--cl-surface)] border border-white/10 rounded-md pl-12 pr-12 py-4 text-lg text-white placeholder:text-zinc-500 focus:outline-none focus:border-[var(--cl-primary)]"
        />
        {state.q && (
          <button onClick={() => update("q", "")} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
            <X size={18} />
          </button>
        )}
      </div>

      {/* Toolbar */}
      <div className="mt-6 flex flex-wrap items-center gap-3 border-b border-white/10 pb-4">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="toggle-filters-btn"
          className="flex items-center gap-2 text-label px-4 py-2 rounded border border-white/10 hover:border-[var(--cl-orange)] transition-colors"
        >
          <SlidersHorizontal size={14} />
          {sidebarOpen ? "Hide Filters" : "Show Filters"}
        </button>
        <div className="text-sm text-zinc-400 ml-2" data-testid="shop-count">
          {loading ? "Loading…" : (
            filtersActive
              ? <>Showing <span className="text-white font-medium">{items.length}</span> filtered result{items.length === 1 ? "" : "s"}</>
              : <>Showing <span className="text-white font-medium">{items.length}</span> card{items.length === 1 ? "" : "s"}</>
          )}
        </div>
        {filtersActive && (
          <button onClick={clearAll} data-testid="clear-all-filters"
                  className="text-xs uppercase tracking-widest text-[var(--cl-orange)] hover:underline">
            Clear all ×
          </button>
        )}
        <select value={state.sort} onChange={(e) => update("sort", e.target.value)}
                data-testid="sort-select"
                className="ml-auto bg-[var(--cl-surface)] border border-white/10 rounded-md px-3 py-2 text-sm">
          <option value="recent">Recently Added</option>
          <option value="price_asc">Price: Low → High</option>
          <option value="price_desc">Price: High → Low</option>
          <option value="year_desc">Year: Newest</option>
          <option value="numbered_asc">Numbered: Low → High</option>
        </select>
      </div>

      <div className={`mt-6 grid gap-8 ${sidebarOpen ? "md:grid-cols-[280px_1fr]" : "grid-cols-1"}`}>
        {/* Sidebar */}
        {sidebarOpen && (
          <aside className="space-y-6" data-testid="shop-sidebar">
            <div className="flex items-center justify-between md:hidden">
              <span className="text-label text-zinc-500">Filters</span>
              <button onClick={() => setSidebarOpen(false)} className="text-zinc-500"><ChevronLeft size={16} /></button>
            </div>

            <FilterGroup title="Sport">
              <Chip active={state.sport === "all"} onClick={() => update("sport", "all")} testid="sport-all">All</Chip>
              {sports.map((s) => (
                <Chip key={s.key} active={state.sport === s.key} onClick={() => update("sport", s.key)} testid={`sport-${s.key}`}>
                  {s.label}
                </Chip>
              ))}
            </FilterGroup>

            <FilterGroup title="Brand">
              <Chip active={!state.brand} onClick={() => { update("brand", ""); update("brand_line", ""); }} testid="brand-all">All Brands</Chip>
              {brands.map((b) => (
                <Chip key={b.key} active={state.brand === b.key}
                      onClick={() => { update("brand", b.key); update("brand_line", ""); }}
                      testid={`brand-${b.key}`}>
                  {b.label}
                </Chip>
              ))}
            </FilterGroup>

            {state.brand && linesForBrand.length > 0 && (
              <FilterGroup title={`${brands.find(b => b.key === state.brand)?.label || ""} Sets`}>
                <Chip active={!state.brand_line} onClick={() => update("brand_line", "")} testid="brand-line-all">All</Chip>
                {linesForBrand.map((bl) => (
                  <Chip key={bl.key} active={state.brand_line === bl.key}
                        onClick={() => update("brand_line", bl.key)} testid={`brand-line-${bl.key}`}>
                    {bl.label}
                  </Chip>
                ))}
              </FilterGroup>
            )}

            <FilterGroup title="Condition">
              <Chip active={state.condition_group === "raw"} onClick={() => update("condition_group", state.condition_group === "raw" ? "" : "raw")} testid="cond-raw">Raw</Chip>
              <Chip active={state.condition_group === "graded"} onClick={() => update("condition_group", state.condition_group === "graded" ? "" : "graded")} testid="cond-graded">Graded</Chip>
            </FilterGroup>

            {state.condition_group === "graded" && (
              <FilterGroup title="Grader">
                {graders.filter((g) => g.key !== "raw").map((g) => (
                  <Chip key={g.key} active={state.grader === g.key}
                        onClick={() => update("grader", state.grader === g.key ? "" : g.key)}
                        testid={`grader-${g.key}`}>
                    {g.label}
                  </Chip>
                ))}
              </FilterGroup>
            )}

            <FilterGroup title="Attributes">
              {attrOpts.map((a) => (
                <Chip key={a.key} active={state.attrs.includes(a.key)} onClick={() => toggleAttr(a.key)}
                      testid={`attr-${a.key}`}>
                  {a.label}
                </Chip>
              ))}
            </FilterGroup>

            <FilterGroup title="Year">
              <div className="flex gap-2 w-full">
                <input type="number" placeholder="From" value={state.year_from} onChange={(e) => update("year_from", e.target.value)}
                       data-testid="year-from" className="w-full bg-black border border-white/10 rounded px-3 py-2 text-sm" />
                <input type="number" placeholder="To" value={state.year_to} onChange={(e) => update("year_to", e.target.value)}
                       data-testid="year-to" className="w-full bg-black border border-white/10 rounded px-3 py-2 text-sm" />
              </div>
            </FilterGroup>

            <FilterGroup title="Set">
              <input list="sets-list" value={state.set_name} onChange={(e) => update("set_name", e.target.value)}
                     placeholder="e.g. Prizm" data-testid="filter-set"
                     className="w-full bg-black border border-white/10 rounded px-3 py-2 text-sm" />
              <datalist id="sets-list">{facets.set_name.map((s) => <option key={s} value={s} />)}</datalist>
            </FilterGroup>

            <FilterGroup title="Parallel">
              <input list="parallel-list" value={state.parallel} onChange={(e) => update("parallel", e.target.value)}
                     placeholder="e.g. Silver, /75" data-testid="filter-parallel"
                     className="w-full bg-black border border-white/10 rounded px-3 py-2 text-sm" />
              <datalist id="parallel-list">{facets.parallel.map((p) => <option key={p} value={p} />)}</datalist>
            </FilterGroup>
          </aside>
        )}

        {/* Main content */}
        <main>
          <div className={`grid gap-4 ${sidebarOpen ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"}`} data-testid="shop-grid">
            {!loading && items.length === 0 && (
              <div className="col-span-full text-zinc-500 py-16 text-center">
                <p className="mb-4">No cards match those filters.</p>
                {filtersActive && <button onClick={clearAll} className="btn-outline" data-testid="empty-clear-filters">Clear all filters</button>}
              </div>
            )}
            {items.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </main>
      </div>
    </div>
  );
}

function FilterGroup({ title, children }) {
  return (
    <div>
      <div className="text-label text-zinc-500 mb-3 border-b border-white/5 pb-2">{title}</div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-xs px-3 py-1.5 rounded border transition-colors ${active ? "bg-white text-black border-white" : "border-white/10 text-zinc-300 hover:border-[var(--cl-orange)]"}`}
    >
      {children}
    </button>
  );
}
